import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import {
  Banknote,
  CheckCircle2,
  FileSignature,
  Gift,
  Save,
  UserRound
} from 'lucide-react';

import { db } from '../../core/firebase/firebase';

const DEFAULT_BONUSES = [
  { term: '1er trimestre', amount: '', paymentDate: '' },
  { term: '2e trimestre', amount: '', paymentDate: '' },
  { term: '3e trimestre', amount: '', paymentDate: '' }
];

const EMPTY_CONTRACT = {
  startDate: '',
  effectiveDate: '',
  position: 'Enseignant(e)',
  weeklyHours: '',
  baseSalary: '',
  observations: '',
  bonuses: DEFAULT_BONUSES
};

function teacherName(teacher) {
  const notice = teacher?.teacherNotice || {};
  return [notice.firstName, notice.lastName].filter(Boolean).join(' ')
    || teacher?.fullname
    || teacher?.fullName
    || teacher?.username
    || teacher?.email
    || 'Enseignant';
}

function normalizeBonuses(items) {
  const byTerm = new Map((Array.isArray(items) ? items : []).map((item) => [item.term, item]));
  return DEFAULT_BONUSES.map((defaultItem) => {
    const existing = byTerm.get(defaultItem.term) || {};
    return {
      term: defaultItem.term,
      amount: existing.amount ?? '',
      paymentDate: existing.paymentDate || ''
    };
  });
}

export default function TeacherContractsAdmin() {
  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState('');
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingContract, setLoadingContract] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasContract, setHasContract] = useState(false);

  useEffect(() => {
    async function loadTeachers() {
      setLoadingTeachers(true);
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => ['teacher', 'enseignant'].includes(String(item.accountType || item.role || '').toLowerCase()))
          .filter((item) => item.status !== 'disabled')
          .sort((a, b) => teacherName(a).localeCompare(teacherName(b), 'fr'));
        setTeachers(rows);
      } catch (loadError) {
        console.error('Chargement enseignants contrats :', loadError);
        setError('Impossible de charger la liste des enseignants.');
      } finally {
        setLoadingTeachers(false);
      }
    }
    loadTeachers();
  }, []);

  const selectedTeacher = useMemo(
    () => teachers.find((teacher) => teacher.id === teacherId) || null,
    [teachers, teacherId]
  );

  useEffect(() => {
    if (!teacherId) {
      setForm(EMPTY_CONTRACT);
      setHasContract(false);
      return;
    }

    let cancelled = false;
    async function loadContract() {
      setLoadingContract(true);
      setError('');
      setSuccess('');
      try {
        const snapshot = await getDoc(doc(db, 'teacherContracts', teacherId));
        if (cancelled) return;
        if (snapshot.exists()) {
          const data = snapshot.data();
          setForm({
            startDate: data.startDate || '',
            effectiveDate: data.effectiveDate || '',
            position: data.position || 'Enseignant(e)',
            weeklyHours: data.weeklyHours ?? '',
            baseSalary: data.baseSalary ?? '',
            observations: data.observations || '',
            bonuses: normalizeBonuses(data.bonuses)
          });
          setHasContract(true);
        } else {
          setForm(EMPTY_CONTRACT);
          setHasContract(false);
        }
      } catch (loadError) {
        console.error('Chargement contrat enseignant :', loadError);
        if (!cancelled) setError('Impossible de charger le contrat de cet enseignant.');
      } finally {
        if (!cancelled) setLoadingContract(false);
      }
    }
    loadContract();
    return () => { cancelled = true; };
  }, [teacherId]);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function updateBonus(index, field, value) {
    setForm((current) => ({
      ...current,
      bonuses: current.bonuses.map((bonus, bonusIndex) => bonusIndex === index ? { ...bonus, [field]: value } : bonus)
    }));
  }

  async function saveContract(event) {
    event.preventDefault();
    if (!teacherId || !selectedTeacher) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const weeklyHours = form.weeklyHours === '' ? null : Number(form.weeklyHours);
      const baseSalary = form.baseSalary === '' ? null : Number(form.baseSalary);
      const bonuses = form.bonuses.map((bonus) => ({
        term: bonus.term,
        amount: bonus.amount === '' ? null : Number(bonus.amount),
        paymentDate: bonus.paymentDate || ''
      }));

      await setDoc(doc(db, 'teacherContracts', teacherId), {
        teacherId,
        teacherName: teacherName(selectedTeacher),
        contractType: 'CDI',
        startDate: form.startDate,
        effectiveDate: form.effectiveDate,
        position: form.position.trim(),
        weeklyHours,
        baseSalary,
        observations: form.observations.trim(),
        bonuses,
        updatedAt: serverTimestamp(),
        ...(hasContract ? {} : { createdAt: serverTimestamp() })
      }, { merge: true });

      setHasContract(true);
      setSuccess(hasContract ? 'Le contrat a été mis à jour.' : 'Le contrat CDI a été créé.');
    } catch (saveError) {
      console.error('Enregistrement contrat enseignant :', saveError);
      setError('Impossible d’enregistrer ce contrat.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="teacher-contract-admin-v1">
      <article className="panel teacher-contract-admin-selector-v1">
        <div className="personnel-section-heading">
          <span className="personnel-section-icon"><FileSignature size={22} /></span>
          <div><p className="eyebrow">DOSSIER CONTRACTUEL</p><h2>Contrats enseignants</h2><p className="muted">Créez ou mettez à jour le CDI et les primes trimestrielles d’un enseignant.</p></div>
        </div>

        <label className="teacher-contract-admin-teacher-v1">Enseignant
          <select value={teacherId} onChange={(event) => setTeacherId(event.target.value)} disabled={loadingTeachers}>
            <option value="">Sélectionner un enseignant</option>
            {teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacherName(teacher)}</option>)}
          </select>
        </label>
      </article>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="success-note"><CheckCircle2 size={18} />{success}</div>}

      {!teacherId ? (
        <div className="complaint-empty-v3 teacher-contract-admin-empty-v1">
          <UserRound size={30} />
          <strong>Choisissez un enseignant</strong>
          <span>Le formulaire de son contrat apparaîtra ici.</span>
        </div>
      ) : loadingContract ? (
        <div className="compact-empty">Chargement du contrat…</div>
      ) : (
        <form className="teacher-contract-admin-form-v1" onSubmit={saveContract}>
          <article className="panel teacher-contract-admin-section-v1">
            <div className="teacher-contract-admin-section-head-v1">
              <div><p className="eyebrow">CONTRAT</p><h3>{hasContract ? 'Modifier le CDI' : 'Créer le CDI'}</h3></div>
              <span className="teacher-contract-cdi-badge-v1">CDI</span>
            </div>

            <div className="teacher-contract-admin-grid-v1">
              <label>Date de début<input type="date" value={form.startDate} onChange={(event) => updateField('startDate', event.target.value)} required /></label>
              <label>Date d’effet<input type="date" value={form.effectiveDate} onChange={(event) => updateField('effectiveDate', event.target.value)} required /></label>
              <label>Fonction<input value={form.position} onChange={(event) => updateField('position', event.target.value)} required placeholder="Enseignant(e)" /></label>
              <label>Temps de travail hebdomadaire<input type="number" min="0" step="0.5" value={form.weeklyHours} onChange={(event) => updateField('weeklyHours', event.target.value)} placeholder="Ex. 40" /></label>
              <label>Salaire de base (DT)<input type="number" min="0" step="0.001" value={form.baseSalary} onChange={(event) => updateField('baseSalary', event.target.value)} required placeholder="Ex. 1200" /></label>
            </div>

            <label className="teacher-contract-admin-observations-v1">Observations<textarea value={form.observations} onChange={(event) => updateField('observations', event.target.value)} placeholder="Informations complémentaires éventuelles…" /></label>
          </article>

          <article className="panel teacher-contract-admin-section-v1">
            <div className="teacher-contract-admin-section-head-v1">
              <div><p className="eyebrow">PRIMES</p><h3>Primes trimestrielles</h3><p>Chaque prime est enregistrée séparément avec son montant et sa date de paiement.</p></div>
              <Gift size={22} />
            </div>

            <div className="teacher-contract-bonus-grid-v1">
              {form.bonuses.map((bonus, index) => (
                <div className="teacher-contract-bonus-card-v1" key={bonus.term}>
                  <div><span className="teacher-contract-bonus-number-v1">{index + 1}</span><strong>{bonus.term}</strong></div>
                  <label>Montant (DT)<input type="number" min="0" step="0.001" value={bonus.amount ?? ''} onChange={(event) => updateBonus(index, 'amount', event.target.value)} placeholder="Facultatif" /></label>
                  <label>Date de paiement<input type="date" value={bonus.paymentDate || ''} onChange={(event) => updateBonus(index, 'paymentDate', event.target.value)} /></label>
                </div>
              ))}
            </div>
          </article>

          <div className="teacher-contract-admin-save-v1">
            <div><Banknote size={18} /><span>Les informations seront visibles uniquement par la gérance et l’enseignant concerné.</span></div>
            <button type="submit" className="button button-primary" disabled={submitting}><Save size={17} />{submitting ? 'Enregistrement…' : hasContract ? 'Enregistrer les modifications' : 'Créer le contrat'}</button>
          </div>
        </form>
      )}
    </section>
  );
}
